import Papa from 'papaparse';
import { v4 as uuidv4 } from 'uuid';
import {
  Report,
  ReportData,
  CSVSalesRow,
  CSVAdSpendRow,
  ValidationError,
  JobData
} from '../types/reportTypes';

/**
 * Interface for new CSV format rows (2026+)
 */
interface NewFormatCSVRow {
  'Date'?: string;
  'Name'?: string;
  'Job trade type'?: string;
  'Volume'?: string | number;
  'Lead Source'?: string;
  'Cost'?: string | number;
  'Gross Profit'?: string | number;
  'GP Margin'?: string | number;
  'Comments'?: string;
  [key: string]: string | number | undefined;
}

/**
 * Interface for calculation results
 */
interface CalculatedFields {
  commissionPercentage?: number;
  salesCommission: number;
  salesManagerFee: number;
  companyOH: number;
  netProfit: number;
}

/**
 * Service for processing CSV files containing sales and ad spend data.
 * Handles parsing, validation, and report generation.
 *
 * Supports multiple CSV formats:
 * - Old format (pre-2026): Header-based with currency strings, requires detection of header row
 * - New format (2026+): Clean headers on row 1 with plain numbers
 *
 * Supports multiple calculation methods:
 * - Standard: Tiered commission percentage based on gross margin, 15.5% OH
 * - Cody: Flat 13% OH, 50% of net profit commission
 */
export class CSVProcessingService {
  /**
   * Parse a sales data CSV file
   * @param file CSV file containing sales data
   * @returns Promise resolving to the parsed data and any validation errors
   */
  parseSalesCSV(file: File): Promise<{
    data: CSVSalesRow[];
    errors: ValidationError[];
  }> {
    return new Promise((resolve, reject) => {
      const errors: ValidationError[] = [];
      
      Papa.parse<CSVSalesRow>(file as unknown as string, {
        header: true,
        skipEmptyLines: true,
        complete: (result: Papa.ParseResult<CSVSalesRow>) => {
          const parsedData = result.data as CSVSalesRow[];
          
          // Validate required columns
          if (result.meta.fields) {
            const requiredColumns = [
              'Lead source', 
              'Trade', 
              'Job Info', 
              'Contract Total',
              'Gross Margin %',
              'Gross Profit'
            ];
            
            const missingColumns = requiredColumns.filter(
              col => !result.meta.fields?.includes(col)
            );
            
            if (missingColumns.length > 0) {
              errors.push({
                row: 0,
                column: 'header',
                message: `Missing required columns: ${missingColumns.join(', ')}`
              });
            }
            
            // Note: CP column is optional as we can calculate it if missing
          }
          
          // Validate data rows
          parsedData.forEach((row, index) => {
            // Skip validation for effectively empty rows
            if (this.isEffectivelyEmpty(row)) {
              console.log(`Skipping validation for empty row at index ${index}`);
              return;
            }
            
            // Check for missing required values
            if (!row['Lead source']) {
              errors.push({
                row: index + 1,
                column: 'Lead source',
                message: 'Missing lead source'
              });
            }
            
            if (!row['Contract Total']) {
              errors.push({
                row: index + 1,
                column: 'Contract Total',
                message: 'Missing contract total'
              });
            } else {
              // Check if contract total is a valid number
              const contractTotal = this.parseAmount(row['Contract Total'] as string);
              if (isNaN(contractTotal)) {
                errors.push({
                  row: index + 1,
                  column: 'Contract Total',
                  message: 'Contract total is not a valid number'
                });
              }
            }
          });
          
          resolve({
            data: parsedData,
            errors
          });
        },
        error: (error: Error) => {
          reject(new Error(`Failed to parse sales CSV: ${error.message}`));
        }
      });
    });
  }
  
  /**
   * Parse an ad spend data CSV file
   * @param file CSV file containing ad spend data
   * @returns Promise resolving to the parsed data and any validation errors
   */
  parseAdSpendCSV(file: File): Promise<{
    data: CSVAdSpendRow[];
    errors: ValidationError[];
  }> {
    return new Promise((resolve, reject) => {
      const errors: ValidationError[] = [];
      
      Papa.parse<CSVAdSpendRow>(file as unknown as string, {
        header: true,
        skipEmptyLines: true,
        complete: (result: Papa.ParseResult<CSVAdSpendRow>) => {
          const parsedData = result.data as CSVAdSpendRow[];
          
          // Validate required columns
          if (result.meta.fields) {
            const requiredColumns = ['Lead Source', 'Spend'];
            
            const missingColumns = requiredColumns.filter(
              col => !result.meta.fields?.includes(col)
            );
            
            if (missingColumns.length > 0) {
              errors.push({
                row: 0,
                column: 'header',
                message: `Missing required columns: ${missingColumns.join(', ')}`
              });
            }
          }
          
          // Validate data rows
          parsedData.forEach((row, index) => {
            // Skip validation for effectively empty rows
            if (this.isEffectivelyEmpty(row)) {
              console.log(`Skipping validation for empty row at index ${index}`);
              return;
            }
            
            // Check for missing required values
            if (!row['Lead Source']) {
              errors.push({
                row: index + 1,
                column: 'Lead Source',
                message: 'Missing lead source'
              });
            }
            
            if (!row['Spend']) {
              errors.push({
                row: index + 1,
                column: 'Spend',
                message: 'Missing spend amount'
              });
            } else {
              // Check if spend is a valid number
              const spend = this.parseAmount(row['Spend'] as string);
              if (isNaN(spend)) {
                errors.push({
                  row: index + 1,
                  column: 'Spend',
                  message: 'Spend is not a valid number'
                });
              }
            }
          });
          
          resolve({
            data: parsedData,
            errors
          });
        },
        error: (error: Error) => {
          reject(new Error(`Failed to parse ad spend CSV: ${error.message}`));
        }
      });
    });
  }
  
  /**
   * Process sales and ad spend data to generate a report
   * @param salesData Parsed sales data
   * @param adSpendData Parsed ad spend data
   * @param title Report title
   * @param period Report period (month and year)
   * @param reportType Report type (monthly, quarterly, annual)
   * @param userId User ID of the creator
   * @returns A complete report object
   */
  generateReport(
    salesData: CSVSalesRow[],
    adSpendData: CSVAdSpendRow[],
    title: string,
    period: { month: number; year: number },
    reportType: 'monthly' | 'quarterly' | 'annual',
    userId: string
  ): Report {
    // Process sales data
    const salesBySource: Map<string, { totalSales: number; count: number; grossProfit: number }> = new Map();
    const salesByTrade: Map<string, { totalSales: number; count: number; grossProfit: number }> = new Map();
    const jobData: Array<{
      jobInfo: string;
      leadSource: string;
      trade: string;
      contractTotal: number;
      grossMarginPct: number;
      grossProfit: number;
      salesCommission: number;
      salesManagerFee: number;
      companyOH: number;
      netProfit: number;
    }> = [];
    
    let totalSales = 0;
    let totalGrossProfit = 0;
    let totalSalesCommissions = 0;
    let totalManagerFees = 0;
    let totalCompanyOH = 0;

    // Process each sales row
    salesData.forEach(row => {
      const leadSource = row['Lead source'] || 'Unknown';
      const trade = row['Trade'] || 'Unknown';
      const jobInfo = row['Job Info'] || 'Unknown';
      
      // Parse numeric values
      const contractTotal = this.parseAmount(row['Contract Total'] || '0');
      const grossMarginPct = this.parsePercentage(row['Gross Margin %'] || '0');
      const grossProfit = this.parseAmount(row['Gross Profit'] || '0');
      
      // Calculate commission percentage based on gross margin
      // Calculate commission percentage based on either CSV value or gross margin
      // This value can be used for future calculations if needed
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const commissionPercentageValue: number = row['CP'] 
        ? this.parseAmount(row['CP'] as string) 
        : this.calculateCommissionPercentage(grossMarginPct);
      
      const salesCommission = this.parseAmount(row['Sales Commission'] || '0');
      const salesManagerFee = this.parseAmount(row['Sales Manger Fee:'] || '0');
      const companyOH = this.parseAmount(row['Comapany OH:'] || '0');
      
      // Calculate net profit for this job (excluding ad spend)
      const netProfit = grossProfit - salesCommission - salesManagerFee - companyOH;
      
      // Skip invalid entries
      if (isNaN(contractTotal) || contractTotal <= 0) {
        return;
      }
      
      // Update totals
      totalSales += contractTotal;
      totalGrossProfit += grossProfit;
      totalSalesCommissions += salesCommission;
      totalManagerFees += salesManagerFee;
      totalCompanyOH += companyOH;
      
      // Add to job data
      jobData.push({
        jobInfo,
        leadSource,
        trade,
        contractTotal,
        grossMarginPct,
        grossProfit,
        salesCommission,
        salesManagerFee,
        companyOH,
        netProfit
      });
      
      // Aggregate by source
      if (!salesBySource.has(leadSource)) {
        salesBySource.set(leadSource, { totalSales: 0, count: 0, grossProfit: 0 });
      }
      
      const sourceData = salesBySource.get(leadSource);
      if (sourceData) {
        sourceData.totalSales += contractTotal;
        sourceData.count += 1;
        sourceData.grossProfit += grossProfit;
      }
      
      // Aggregate by trade
      if (!salesByTrade.has(trade)) {
        salesByTrade.set(trade, { totalSales: 0, count: 0, grossProfit: 0 });
      }
      
      const tradeData = salesByTrade.get(trade);
      if (tradeData) {
        tradeData.totalSales += contractTotal;
        tradeData.count += 1;
        tradeData.grossProfit += grossProfit;
      }
    });
    
    // Process ad spend data
    const adSpendBySource: Array<{ source: string; spend: number }> = [];
    let totalAdSpend = 0;
    
    adSpendData.forEach(row => {
      const source = row['Lead Source'] || 'Unknown';
      const spend = this.parseAmount(row['Spend'] || '0');
      
      if (!isNaN(spend) && spend > 0) {
        adSpendBySource.push({ source, spend });
        totalAdSpend += spend;
      }
    });
    
    // Calculate final metrics
    const netProfit = totalGrossProfit - totalAdSpend - totalSalesCommissions - totalManagerFees - totalCompanyOH;
    const netProfitPercent = totalSales > 0 ? netProfit / totalSales : 0;
    const grossProfitPercent = totalSales > 0 ? totalGrossProfit / totalSales : 0;
    
    // Create the report data
    const reportData: ReportData = {
      kpis: {
        adSpend: totalAdSpend,
        totalSales,
        grossProfit: totalGrossProfit,
        salesCommissions: totalSalesCommissions,
        managerFees: totalManagerFees,
        companyOH: totalCompanyOH,
        netProfit,
        netProfitPercent,
        grossProfitPercent
      },
      salesBySource: Array.from(salesBySource.entries()).map(([source, data]) => ({
        source,
        totalSales: data.totalSales,
        count: data.count,
        grossProfit: data.grossProfit
      })),
      adSpendBySource,
      salesByTrade: Array.from(salesByTrade.entries()).map(([trade, data]) => ({
        trade,
        totalSales: data.totalSales,
        count: data.count,
        grossProfit: data.grossProfit
      })),
      jobData
    };
    
    // Create and return the full report
    return {
      id: uuidv4(),
      title,
      reportType,
      period,
      createdBy: userId,
      data: reportData,
      rawFiles: {
        // We're not storing the actual files in this phase, just the data
        // In a future phase with backend storage, these could be file paths
      },
      createdAt: new Date()
    };
  }
  
  // ========================================================================
  // ENHANCED PARSING METHODS (NEW)
  // ========================================================================

  /**
   * Find the header row in a CSV file that contains summary rows before headers.
   * Searches for a row containing "Lead source" (case-insensitive).
   *
   * @param file CSV file to analyze
   * @returns Promise resolving to the index of the header row, or -1 if not found
   */
  async findHeaderRow(file: File): Promise<number> {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: false,
        skipEmptyLines: true,
        complete: (result: Papa.ParseResult<string[]>) => {
          const rows = result.data as string[][];

          // Search for the row containing "Lead source" (case-insensitive)
          const headerRowIndex = rows.findIndex((row) =>
            row.some(
              (cell) =>
                typeof cell === 'string' &&
                cell.toLowerCase().includes('lead source')
            )
          );

          resolve(headerRowIndex);
        },
        error: (error: Error) => {
          reject(
            new Error(`Failed to find header row: ${error.message}`)
          );
        }
      });
    });
  }

  /**
   * Parse a CSV file with automatic header row detection.
   * Useful for old format CSVs that have summary rows before the actual headers.
   *
   * @param file CSV file to parse
   * @returns Promise resolving to parsed data with headers properly detected
   */
  async parseWithHeaderDetection<T>(file: File): Promise<{
    data: T[];
    errors: ValidationError[];
    headerRowIndex: number;
  }> {
    const headerRowIndex = await this.findHeaderRow(file);

    if (headerRowIndex === -1) {
      return {
        data: [],
        errors: [
          {
            row: 0,
            column: 'header',
            message:
              'Could not find header row containing "Lead source". File may be in an unexpected format.'
          }
        ],
        headerRowIndex: -1
      };
    }

    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: false,
        skipEmptyLines: true,
        complete: (result: Papa.ParseResult<string[]>) => {
          const rows = result.data as string[][];

          // Extract headers and data rows
          const headers = rows[headerRowIndex];
          const dataRows = rows.slice(headerRowIndex + 1);

          // Convert to objects using headers
          const parsedData = dataRows.map((row) => {
            const obj: Record<string, string> = {};
            headers.forEach((header, index) => {
              obj[header] = row[index] || '';
            });
            return obj as T;
          });

          resolve({
            data: parsedData,
            errors: [],
            headerRowIndex
          });
        },
        error: (error: Error) => {
          reject(
            new Error(
              `Failed to parse CSV with header detection: ${error.message}`
            )
          );
        }
      });
    });
  }

  /**
   * Parse a new format (2026+) sales CSV file.
   * This format has clean headers on row 1 with plain numeric values.
   *
   * Expected columns:
   * - Date, Name, Job trade type, Volume, Lead Source, Cost, Gross Profit, GP Margin, Comments
   *
   * Filtering rules:
   * - Exclude rows where Comments contain "CANCELLED" (cancelled jobs)
   * - Include "NO PPP" / N/A rows with zeroed GP fields (pending approval, real sales volume)
   *
   * @param file CSV file in new format
   * @returns Promise resolving to JobData array and validation errors
   */
  async parseNewFormatCSV(file: File): Promise<{
    data: JobData[];
    errors: ValidationError[];
  }> {
    return new Promise((resolve, reject) => {
      const errors: ValidationError[] = [];

      Papa.parse<NewFormatCSVRow>(file as unknown as string, {
        header: true,
        skipEmptyLines: true,
        complete: (result: Papa.ParseResult<NewFormatCSVRow>) => {
          const parsedData = result.data as NewFormatCSVRow[];

          // Validate required columns
          if (result.meta.fields) {
            const requiredColumns = [
              'Name',
              'Job trade type',
              'Volume',
              'Lead Source',
              'Gross Profit',
              'GP Margin'
            ];

            const missingColumns = requiredColumns.filter(
              (col) => !result.meta.fields?.includes(col)
            );

            if (missingColumns.length > 0) {
              errors.push({
                row: 0,
                column: 'header',
                message: `Missing required columns: ${missingColumns.join(', ')}`
              });
              resolve({ data: [], errors });
              return;
            }
          }

          // Process and filter data rows
          const jobData: JobData[] = [];

          parsedData.forEach((row, index) => {
            // Skip effectively empty rows
            if (this.isEffectivelyEmpty(row)) {
              return;
            }

            // Filter out CANCELLED rows entirely
            const comments = String(row['Comments'] || '').toUpperCase();
            if (comments.includes('CANCELLED')) {
              return;
            }

            // Check if this is a NO PPP row (pending approval - include with zeroed GP)
            const grossProfitStr = String(row['Gross Profit'] || '').trim();
            const gpMarginStr = String(row['GP Margin'] || '').trim();
            const isNoPPP = comments.includes('NO PPP') ||
              grossProfitStr.toUpperCase() === 'N/A' ||
              gpMarginStr.toUpperCase() === 'N/A' ||
              !grossProfitStr ||
              !gpMarginStr;

            // Parse contract total (Volume) - needed for both regular and NO PPP rows
            const contractTotal =
              typeof row['Volume'] === 'number'
                ? row['Volume']
                : parseFloat(String(row['Volume'] || '0'));

            // Skip rows with zero or negative contract total
            if (isNaN(contractTotal) || contractTotal <= 0) {
              return;
            }

            // For NO PPP / N/A rows: include with real volume but zeroed GP fields
            let grossProfit: number;
            let grossMarginPct: number;

            if (isNoPPP) {
              grossProfit = 0;
              grossMarginPct = 0;
            } else {
              grossProfit =
                typeof row['Gross Profit'] === 'number'
                  ? row['Gross Profit']
                  : parseFloat(String(row['Gross Profit'] || '0'));
              grossMarginPct =
                typeof row['GP Margin'] === 'number'
                  ? row['GP Margin']
                  : parseFloat(String(row['GP Margin'] || '0'));

              // Validate numeric values for regular rows
              if (isNaN(grossProfit) || isNaN(grossMarginPct)) {
                errors.push({
                  row: index + 1,
                  column: 'Volume/Gross Profit/GP Margin',
                  message: 'One or more numeric values are invalid'
                });
                return;
              }
            }

            // Sanitize trade name: strip "ROI " prefix and use first trade only
            const rawTrade = String(row['Job trade type'] || 'Unknown');
            const trade = rawTrade.split(',')[0].trim().replace(/^ROI\s+/i, '');

            // Build JobData (calculations will be done by caller)
            jobData.push({
              jobInfo: String(row['Name'] || 'Unknown'),
              leadSource: String(row['Lead Source'] || 'Unknown'),
              trade,
              contractTotal,
              grossMarginPct,
              grossProfit,
              salesCommission: 0, // To be calculated
              salesManagerFee: 0, // To be calculated
              companyOH: 0, // To be calculated
              netProfit: 0 // To be calculated
            });
          });

          resolve({ data: jobData, errors });
        },
        error: (error: Error) => {
          reject(
            new Error(`Failed to parse new format CSV: ${error.message}`)
          );
        }
      });
    });
  }

  // ========================================================================
  // CALCULATION ENGINES (NEW)
  // ========================================================================

  /**
   * Calculate fields using standard commission calculation method.
   *
   * Rules:
   * - Commission Percentage (CP):
   *   - 0% if gross margin < 40%
   *   - 4% if gross margin 40-50%
   *   - 7% if gross margin 50-60%
   *   - 10% if gross margin >= 60%
   * - Sales Commission = Contract Total × (CP / 100)
   * - Sales Manager Fee = Contract Total × 1%
   * - Company OH = Contract Total × 15.5%
   * - Net Profit = Gross Profit - (Sales Commission + Sales Manager Fee + Company OH)
   *
   * @param contractTotal Contract total amount
   * @param grossProfit Gross profit amount
   * @param grossMarginPct Gross margin as a decimal (e.g., 0.45 for 45%)
   * @returns Calculated fields object
   */
  public calculateStandardFields(
    contractTotal: number,
    grossProfit: number,
    grossMarginPct: number
  ): CalculatedFields {
    // Calculate commission percentage based on gross margin
    const commissionPercentage =
      this.calculateCommissionPercentage(grossMarginPct);

    // Calculate individual components
    const salesCommission = contractTotal * (commissionPercentage / 100);
    const salesManagerFee = contractTotal * 0.01;
    const companyOH = contractTotal * 0.155;

    // Calculate net profit
    const netProfit =
      grossProfit - (salesCommission + salesManagerFee + companyOH);

    return {
      commissionPercentage,
      salesCommission,
      salesManagerFee,
      companyOH,
      netProfit
    };
  }

  /**
   * Calculate fields using Cody commission calculation method.
   *
   * Rules:
   * - Company OH = Contract Total × 13% (flat rate)
   * - Net Profit = Gross Profit - Company OH
   * - Sales Commission = Net Profit ÷ 2 (50% of net profit)
   * - Sales Manager Fee = 0 (not applicable for Cody)
   *
   * @param contractTotal Contract total amount
   * @param grossProfit Gross profit amount
   * @returns Calculated fields object
   */
  public calculateCodyFields(
    contractTotal: number,
    grossProfit: number
  ): CalculatedFields {
    // Flat 13% company overhead
    const companyOH = contractTotal * 0.13;

    // Net profit after overhead
    const netProfit = grossProfit - companyOH;

    // Sales commission is 50% of net profit
    const salesCommission = netProfit / 2;

    // No manager fee for Cody
    const salesManagerFee = 0;

    return {
      salesCommission,
      salesManagerFee,
      companyOH,
      netProfit
    };
  }

  // ========================================================================
  // UNIFIED ENTRY POINT (NEW)
  // ========================================================================

  /**
   * Generate a complete ReportData object from sales and ad spend CSV files.
   * This is the unified entry point that handles format detection and calculation method selection.
   *
   * Process:
   * 1. Detect CSV format (new 2026+ vs old format)
   * 2. Parse sales data with appropriate parser
   * 3. Apply calculation method (standard vs Cody)
   * 4. Parse ad spend data if provided
   * 5. Aggregate by source and trade
   * 6. Build and return ReportData
   *
   * @param salesFile Sales CSV file
   * @param adSpendFile Ad spend CSV file (optional)
   * @param reportType Type of report ('standard' or 'cody')
   * @param period Report period (month and year)
   * @returns Promise resolving to complete ReportData object
   */
  async generateReportFromFiles(
    salesFile: File,
    adSpendFile: File | null,
    reportType: 'standard' | 'cody',
    period: { month: number; year: number }
  ): Promise<ReportData> {
    // Step 1: Detect CSV format
    const formatDetectionResult = await this.detectCSVFormat(salesFile);

    if (!formatDetectionResult.success) {
      throw new Error(
        `Failed to detect CSV format: ${formatDetectionResult.error}`
      );
    }

    const isNewFormat = formatDetectionResult.isNewFormat;

    // Step 2: Parse sales data
    let jobData: JobData[];
    let parseErrors: ValidationError[];

    if (isNewFormat) {
      // New format: use parseNewFormatCSV
      const result = await this.parseNewFormatCSV(salesFile);
      jobData = result.data;
      parseErrors = result.errors;

      // Apply calculations to each job
      jobData = jobData.map((job) => {
        const calculated =
          reportType === 'cody'
            ? this.calculateCodyFields(job.contractTotal, job.grossProfit)
            : this.calculateStandardFields(
                job.contractTotal,
                job.grossProfit,
                job.grossMarginPct
              );

        return {
          ...job,
          commissionPercentage: calculated.commissionPercentage,
          salesCommission: calculated.salesCommission,
          salesManagerFee: calculated.salesManagerFee,
          companyOH: calculated.companyOH,
          netProfit: calculated.netProfit
        };
      });
    } else {
      // Old format: use parseWithHeaderDetection + manual parsing
      const result =
        await this.parseWithHeaderDetection<CSVSalesRow>(salesFile);
      parseErrors = result.errors;

      // Convert CSVSalesRow to JobData with calculations
      const parsedJobs: JobData[] = [];

      result.data.forEach((row) => {
        // Skip effectively empty rows
        if (this.isEffectivelyEmpty(row)) {
          return;
        }

        // Skip totals/summary rows (old format CSVs have a "Totals" row at the end)
        const jobInfoRaw = (row['Job Info'] || '').trim();
        if (jobInfoRaw.toLowerCase() === 'totals') {
          return;
        }

        const jobInfo = jobInfoRaw || 'Unknown';
        const leadSource = row['Lead source'] || 'Unknown';
        // Sanitize trade name: strip "ROI " prefix and use first trade only
        const rawTrade = row['Trade'] || 'Unknown';
        const trade = rawTrade.split(',')[0].trim().replace(/^ROI\s+/i, '');

        // Parse numeric values from old format (with currency symbols)
        const contractTotal = this.parseAmount(
          row['Contract Total'] || '0'
        );
        const grossMarginPct = this.parsePercentage(
          row['Gross Margin %'] || '0'
        );
        const grossProfit = this.parseAmount(row['Gross Profit'] || '0');

        // Skip invalid entries
        if (isNaN(contractTotal) || contractTotal <= 0) {
          return;
        }

        // Calculate fields based on report type
        const calculated =
          reportType === 'cody'
            ? this.calculateCodyFields(contractTotal, grossProfit)
            : this.calculateStandardFields(
                contractTotal,
                grossProfit,
                grossMarginPct
              );

        parsedJobs.push({
          jobInfo,
          leadSource,
          trade,
          contractTotal,
          grossMarginPct,
          grossProfit,
          commissionPercentage: calculated.commissionPercentage,
          salesCommission: calculated.salesCommission,
          salesManagerFee: calculated.salesManagerFee,
          companyOH: calculated.companyOH,
          netProfit: calculated.netProfit
        });
      });

      jobData = parsedJobs;
    }

    // Check for parsing errors
    if (parseErrors.length > 0) {
      console.warn('CSV parsing encountered errors:', parseErrors);
    }

    // Step 3: Parse ad spend data if provided
    let adSpendData: { source: string; spend: number }[] = [];

    if (adSpendFile) {
      adSpendData = await this.parseAdSpendFromFile(adSpendFile);
    }

    // Step 4: Aggregate data
    const salesBySource: Map<
      string,
      { totalSales: number; count: number; grossProfit: number }
    > = new Map();
    const salesByTrade: Map<
      string,
      { totalSales: number; count: number; grossProfit: number }
    > = new Map();

    let totalSales = 0;
    let totalGrossProfit = 0;
    let totalSalesCommissions = 0;
    let totalManagerFees = 0;
    let totalCompanyOH = 0;

    jobData.forEach((job) => {
      // Update totals
      totalSales += job.contractTotal;
      totalGrossProfit += job.grossProfit;
      totalSalesCommissions += job.salesCommission;
      totalManagerFees += job.salesManagerFee;
      totalCompanyOH += job.companyOH;

      // Aggregate by source
      if (!salesBySource.has(job.leadSource)) {
        salesBySource.set(job.leadSource, {
          totalSales: 0,
          count: 0,
          grossProfit: 0
        });
      }
      const sourceData = salesBySource.get(job.leadSource)!;
      sourceData.totalSales += job.contractTotal;
      sourceData.count += 1;
      sourceData.grossProfit += job.grossProfit;

      // Aggregate by trade
      if (!salesByTrade.has(job.trade)) {
        salesByTrade.set(job.trade, {
          totalSales: 0,
          count: 0,
          grossProfit: 0
        });
      }
      const tradeData = salesByTrade.get(job.trade)!;
      tradeData.totalSales += job.contractTotal;
      tradeData.count += 1;
      tradeData.grossProfit += job.grossProfit;
    });

    // Calculate total ad spend
    const totalAdSpend = adSpendData.reduce(
      (sum, item) => sum + item.spend,
      0
    );

    // Calculate final metrics
    // For Cody reports: Net Profit = GP - OH (per-job, before commission split)
    //   Commission is a separate payout metric, not deducted from net profit
    // For Standard reports: Net Profit = GP - Commission - Manager - OH - Ad Spend
    const totalPerJobNetProfit = jobData.reduce((sum, job) => sum + job.netProfit, 0);
    const netProfit = reportType === 'cody'
      ? totalPerJobNetProfit - totalAdSpend
      : totalGrossProfit - totalAdSpend - totalSalesCommissions - totalManagerFees - totalCompanyOH;
    const netProfitPercent = totalSales > 0 ? netProfit / totalSales : 0;
    const grossProfitPercent =
      totalSales > 0 ? totalGrossProfit / totalSales : 0;

    // Step 5: Build and return ReportData
    return {
      kpis: {
        adSpend: totalAdSpend,
        totalSales,
        grossProfit: totalGrossProfit,
        salesCommissions: totalSalesCommissions,
        managerFees: totalManagerFees,
        companyOH: totalCompanyOH,
        netProfit,
        netProfitPercent,
        grossProfitPercent
      },
      salesBySource: Array.from(salesBySource.entries()).map(
        ([source, data]) => ({
          source,
          totalSales: data.totalSales,
          count: data.count,
          grossProfit: data.grossProfit
        })
      ),
      adSpendBySource: adSpendData,
      salesByTrade: Array.from(salesByTrade.entries()).map(
        ([trade, data]) => ({
          trade,
          totalSales: data.totalSales,
          count: data.count,
          grossProfit: data.grossProfit
        })
      ),
      jobData,
      period: {
        month: period.month,
        year: period.year
      }
    };
  }

  /**
   * Parse ad spend from a file with automatic header detection.
   * Ad spend CSVs may have blank rows before the "Lead Source, Spend" header.
   * Also filters out totals rows (rows with empty source name).
   *
   * @param file Ad spend CSV file
   * @returns Array of source/spend pairs
   */
  async parseAdSpendFromFile(
    file: File
  ): Promise<{ source: string; spend: number }[]> {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: false,
        skipEmptyLines: false,
        complete: (result: Papa.ParseResult<string[]>) => {
          const rows = result.data as string[][];

          // Find the header row containing "Lead Source" and "Spend"
          let headerRowIndex = -1;
          let sourceColIndex = -1;
          let spendColIndex = -1;

          for (let i = 0; i < Math.min(rows.length, 10); i++) {
            const row = rows[i];
            for (let j = 0; j < row.length; j++) {
              const cell = (row[j] || '').trim().toLowerCase();
              if (cell === 'lead source') {
                sourceColIndex = j;
                headerRowIndex = i;
              }
              if (cell === 'spend') {
                spendColIndex = j;
              }
            }
            if (headerRowIndex >= 0) break;
          }

          if (headerRowIndex === -1 || sourceColIndex === -1 || spendColIndex === -1) {
            console.warn('Ad spend CSV: Could not find "Lead Source" and "Spend" headers');
            resolve([]);
            return;
          }

          // Parse data rows after the header
          const adSpendData: { source: string; spend: number }[] = [];
          for (let i = headerRowIndex + 1; i < rows.length; i++) {
            const row = rows[i];
            const source = (row[sourceColIndex] || '').trim();
            const spendStr = (row[spendColIndex] || '').trim();

            // Skip rows with empty source (totals rows) or empty spend
            if (!source || !spendStr) continue;

            const spend = this.parseAmount(spendStr);

            // Skip invalid or zero spend
            if (isNaN(spend) || spend <= 0) continue;

            adSpendData.push({ source, spend });
          }

          resolve(adSpendData);
        },
        error: (error: Error) => {
          reject(new Error(`Failed to parse ad spend CSV: ${error.message}`));
        }
      });
    });
  }

  /**
   * Detect the format of a sales CSV file.
   * Checks if the first data row contains "Volume" header (new format) or "Contract Total" (old format).
   *
   * @param file CSV file to analyze
   * @returns Promise resolving to format detection result
   */
  private async detectCSVFormat(file: File): Promise<{
    success: boolean;
    isNewFormat: boolean;
    error?: string;
  }> {
    return new Promise((resolve) => {
      Papa.parse(file, {
        header: false,
        preview: 10, // Only parse first 10 rows for detection
        skipEmptyLines: true,
        complete: (result: Papa.ParseResult<string[]>) => {
          const rows = result.data as string[][];

          if (rows.length === 0) {
            resolve({
              success: false,
              isNewFormat: false,
              error: 'CSV file is empty'
            });
            return;
          }

          // Search for header indicators in first few rows
          let foundNewFormat = false;
          let foundOldFormat = false;

          for (const row of rows) {
            const rowStr = row.join('|').toLowerCase();

            if (rowStr.includes('volume')) {
              foundNewFormat = true;
              break;
            }

            if (rowStr.includes('contract total')) {
              foundOldFormat = true;
              break;
            }
          }

          // Determine format
          if (foundNewFormat) {
            resolve({ success: true, isNewFormat: true });
          } else if (foundOldFormat) {
            resolve({ success: true, isNewFormat: false });
          } else {
            resolve({
              success: false,
              isNewFormat: false,
              error:
                'Could not detect CSV format. Expected "Volume" (new format) or "Contract Total" (old format) header.'
            });
          }
        },
        error: (error: Error) => {
          resolve({
            success: false,
            isNewFormat: false,
            error: `CSV parsing error: ${error.message}`
          });
        }
      });
    });
  }

  // ========================================================================
  // HELPER METHODS (ENHANCED)
  // ========================================================================

  /**
   * Helper method to parse currency amounts from string values.
   * Made public for external use.
   *
   * @param value String value with potential currency formatting
   * @returns Parsed number value
   */
  public parseAmount(value: string): number {
    // Remove currency symbols, commas, etc.
    const cleanValue = value.replace(/[$,]/g, '').trim();
    return parseFloat(cleanValue);
  }

  /**
   * Helper method to parse percentage values from string values.
   * Made public for external use.
   *
   * @param value String value with potential percentage formatting
   * @returns Parsed decimal value (e.g., 50% becomes 0.5)
   */
  public parsePercentage(value: string): number {
    // Remove percentage symbol
    const cleanValue = value.replace(/%/g, '').trim();
    return parseFloat(cleanValue) / 100;
  }

  /**
   * Calculate commission percentage (CP) multiplier based on gross margin percentage.
   *
   * Brackets:
   * - < 40%: 0%
   * - 40-50%: 4%
   * - 50-60%: 7%
   * - >= 60%: 10%
   *
   * @param grossMarginPct Gross margin percentage as a decimal (e.g., 0.45 for 45%)
   * @returns Commission percentage multiplier (0, 4, 7, or 10)
   */
  private calculateCommissionPercentage(grossMarginPct: number): number {
    // Convert decimal to percentage for comparison
    const grossMarginPercentage = grossMarginPct * 100;

    if (grossMarginPercentage < 40) {
      return 0;
    } else if (grossMarginPercentage >= 40 && grossMarginPercentage < 50) {
      return 4;
    } else if (grossMarginPercentage >= 50 && grossMarginPercentage < 60) {
      return 7;
    } else {
      return 10;
    }
  }

  /**
   * Check if a row is effectively empty (all fields are empty or whitespace)
   * @param row The row object to check
   * @returns True if the row has no meaningful data
   */
  private isEffectivelyEmpty(row: Record<string, unknown>): boolean {
    // If row is null or undefined, it's empty
    if (!row) return true;
    
    // Check if all values are empty or just whitespace
    return Object.values(row).every(value => {
      if (value === null || value === undefined || value === '') return true;
      if (typeof value === 'string' && value.trim() === '') return true;
      return false;
    });
  }
}

// Create and export a singleton instance
export const csvProcessingService = new CSVProcessingService();